import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { User, Mail, Lock, Moon, Sun, DollarSign, AlertCircle, Layout, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../context/AuthContext';
import { usePreferences } from '../../context/PreferencesContext';
import PDFUtilities from '../../components/settings/pdf/PDFUtilities';

interface ProfileFormData {
  name: string;
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface BudgetFormData {
  monthlyIncome: string;
  housingBudget: string;
  foodBudget: string;
  transportationBudget: string;
  entertainmentBudget: string;
  savingsGoal: string;
}

const Settings: React.FC = () => {
  const { state, updateUser } = useAuth();
  const { user } = state;
  const { preferences, updateDashboardLayout, resetDashboardLayout, updateTheme, updateCurrency, updateDateFormat } = usePreferences();
  
  const [activeTab, setActiveTab] = useState<'profile' | 'appearance' | 'budget' | 'dashboard' | 'pdf'>('profile');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return preferences.theme === 'dark';
  });
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { register: registerProfile, handleSubmit: handleProfileSubmit, formState: { errors: profileErrors } } = useForm<ProfileFormData>({
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    }
  });
  
  const { register: registerBudget, handleSubmit: handleBudgetSubmit, formState: { errors: budgetErrors } } = useForm<BudgetFormData>({
    defaultValues: {
      monthlyIncome: '4000',
      housingBudget: '1200',
      foodBudget: '500',
      transportationBudget: '300',
      entertainmentBudget: '200',
      savingsGoal: '800',
    }
  });
  
  const onProfileSubmit = async (data: ProfileFormData) => {
    setIsLoading(true);
    setUpdateSuccess(false);
    setUpdateError(null);
    
    try {
      if (data.newPassword) {
        // Handle password change
      }
      
      setUpdateSuccess(true);
      
      // Clear password fields
      data.currentPassword = '';
      data.newPassword = '';
      data.confirmPassword = '';
    } catch (err: any) {
      setUpdateError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };
  
  const onBudgetSubmit = async (data: BudgetFormData) => {
    setIsLoading(true);
    setUpdateSuccess(false);
    setUpdateError(null);
    
    try {
      setUpdateSuccess(true);
    } catch (err: any) {
      setUpdateError(err.response?.data?.message || 'Failed to update budget settings');
    } finally {
      setIsLoading(false);
    }
  };
  
  const toggleDarkMode = () => {
    const newTheme = isDarkMode ? 'light' : 'dark';
    setIsDarkMode(!isDarkMode);
    updateTheme(newTheme);
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateCurrency(e.target.value);
  };

  const handleDateFormatChange = (format: string) => {
    updateDateFormat(format);
  };

  const handleResetDashboard = () => {
    resetDashboardLayout();
    setUpdateSuccess(true);
    setTimeout(() => setUpdateSuccess(false), 3000);
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
      
      <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <button
          className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${
            activeTab === 'profile'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button
          className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${
            activeTab === 'appearance'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('appearance')}
        >
          Appearance
        </button>
        <button
          className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${
            activeTab === 'dashboard'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard Layout
        </button>
        <button
          className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${
            activeTab === 'budget'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('budget')}
        >
          Budget Goals
        </button>
        <button
          className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${
            activeTab === 'pdf'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('pdf')}
        >
          PDF Tools
        </button>
      </div>
      
      {updateSuccess && (
        <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-md">
          <p className="text-green-700 dark:text-green-300">Settings updated successfully!</p>
        </div>
      )}
      
      {updateError && (
        <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 mr-2 mt-0.5" />
          <p className="text-red-700 dark:text-red-300">{updateError}</p>
        </div>
      )}
      
      {activeTab === 'profile' && (
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-6">
              <Input
                label="Full Name"
                leftIcon={<User className="h-5 w-5 text-gray-400" />}
                error={profileErrors.name?.message}
                {...registerProfile('name', { 
                  required: 'Name is required',
                  minLength: {
                    value: 2,
                    message: 'Name must be at least 2 characters'
                  }
                })}
              />
              
              <Input
                label="Email Address"
                type="email"
                leftIcon={<Mail className="h-5 w-5 text-gray-400" />}
                error={profileErrors.email?.message}
                {...registerProfile('email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
              />
              
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Change Password</h3>
                
                <div className="space-y-4">
                  <Input
                    label="Current Password"
                    type="password"
                    leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
                    error={profileErrors.currentPassword?.message}
                    {...registerProfile('currentPassword', {
                      validate: (value, formValues) => {
                        if (formValues.newPassword && !value) {
                          return 'Current password is required to set a new password';
                        }
                        return true;
                      }
                    })}
                  />
                  
                  <Input
                    label="New Password"
                    type="password"
                    leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
                    error={profileErrors.newPassword?.message}
                    {...registerProfile('newPassword', {
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters'
                      }
                    })}
                  />
                  
                  <Input
                    label="Confirm New Password"
                    type="password"
                    leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
                    error={profileErrors.confirmPassword?.message}
                    {...registerProfile('confirmPassword', {
                      validate: (value, formValues) => {
                        if (formValues.newPassword && value !== formValues.newPassword) {
                          return 'Passwords do not match';
                        }
                        return true;
                      }
                    })}
                  />
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button type="submit" isLoading={isLoading}>
                  Save Changes
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
      
      {activeTab === 'appearance' && (
        <Card>
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Theme</h3>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={toggleDarkMode}
                    className={`flex items-center justify-center w-12 h-6 rounded-full ${
                      isDarkMode ? 'bg-blue-600' : 'bg-gray-200'}`}>
                    <div
                      className={`w-4 h-4 rounded-full transform transition-transform ${
                        isDarkMode ? 'translate-x-2 bg-white' : '-translate-x-2 bg-gray-600'
                      }`}
                    />
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {isDarkMode ? 'Dark Mode' : 'Light Mode'}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'pdf' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                PDF Tools
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PDFUtilities />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Settings;